import apiClient from './apiClient';

/** Alinhado ao backend; tags opcionais com `include_tags=true`. */
export interface ClientTag {
  id: number;
  name: string;
  slug: string;
  color: string;
  owner_user_id?: number | null;
}

export interface Client {
  id: number;
  name?: string;
  phone_number: string;
  email?: string;
  document?: string;
  skip_bot_menu?: boolean;
  created_at?: string;
  tags?: ClientTag[];
}

export interface ClientSearchResponse {
  success: boolean;
  data: {
    clients: Client[];
  };
}

export interface ClientUpdateResponse {
  success: boolean;
  data: Client;
  message: string;
}

export interface ClientListResponse {
  success: boolean;
  data: {
    clients: Client[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

class ClientService {
  /**
   * Listar clientes do admin com paginação
   */
  async listClients(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      tag_ids?: string;
      tag_match?: 'any' | 'all';
      include_tags?: boolean;
    } = {}
  ): Promise<ClientListResponse> {
    const { page = 1, limit = 20, search = '', tag_ids, tag_match, include_tags } = params;
    const query = new URLSearchParams();
    query.set('page', String(page));
    query.set('limit', String(limit));
    if (search.trim()) query.set('search', search.trim());
    if (tag_ids && tag_ids.trim()) query.set('tag_ids', tag_ids.trim());
    if (tag_match) query.set('tag_match', tag_match);
    if (include_tags) query.set('include_tags', 'true');
    const response = await apiClient.get(`/clients/list?${query.toString()}`);
    return response.data;
  }

  /**
   * Buscar clientes por nome, telefone ou documento
   */
  async searchClients(search: string, limit: number = 10): Promise<ClientSearchResponse> {
    try {
      const response = await apiClient.get(`/clients/search?search=${encodeURIComponent(search)}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      // Se for erro de autenticação, retornar lista vazia
      if (error.response?.status === 401) {
        return {
          success: true,
          data: {
            clients: []
          }
        };
      }
      throw error;
    }
  }

  /**
   * Atualizar dados do cliente (name, email, document, skip_bot_menu)
   */
  async updateClient(id: number, data: { name?: string; email?: string; document?: string; skip_bot_menu?: boolean }): Promise<ClientUpdateResponse> {
    const response = await apiClient.put(`/clients/${id}`, data);
    return response.data;
  }
}

export const clientService = new ClientService();
